// `PasswordStrengthMeter` — DS-GAP composite for the auth pages.
//
// DESIGN.md §1.1, §5: composes DS `ProgressBar` + a localized strength label.
// 4 segments mapped to zxcvbn's score 0–4. Tone shifts negative → warning →
// positive as the score climbs. If DS Wave E later promotes a primitive,
// swap this in place.
//
// // DS-GAP: PasswordStrengthMeter (per DESIGN §5)

import type { CSSProperties } from 'react';

import { useT } from '@barel/i18n';

import type { PasswordScore } from '../lib/usePasswordStrength';

export interface PasswordStrengthMeterProps {
  /** zxcvbn score (0–4); `null` while the analyzer is still loading. */
  score: PasswordScore | null;
  /** True once the score has been computed at least once (drives label visibility). */
  ready: boolean;
}

interface SegmentTone {
  /** CSS color for filled segments. */
  filled: string;
  /** Translation suffix for the label (`auth.signUp.fields.password.strength.{n}`). */
  labelKey: '0' | '1' | '2' | '3' | '4';
}

function toneFor(score: PasswordScore): SegmentTone {
  switch (score) {
    case 0:
    case 1:
      return { filled: 'var(--error)', labelKey: String(score) as '0' | '1' };
    case 2:
      return { filled: 'var(--warning)', labelKey: '2' };
    case 3:
    case 4:
    default:
      return { filled: 'var(--success)', labelKey: String(score) as '3' | '4' };
  }
}

const TRACK_BG = 'var(--bg-elevated)';

const SEGMENT_BASE: CSSProperties = {
  flex: '1 1 0',
  height: '6px',
  borderRadius: 'var(--radius-sm)',
  background: TRACK_BG,
  transition: 'background-color var(--duration-fast) var(--ease-out)',
};

export function PasswordStrengthMeter({ score, ready }: PasswordStrengthMeterProps) {
  const t = useT('auth');
  const filledCount = score === null ? 0 : score + 1;
  const tone = score === null ? null : toneFor(score);

  return (
    <div
      data-region="auth-password-strength"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={4}
      aria-valuenow={score ?? 0}
      aria-label={t('signUp.fields.password.strength.label')}
      className="flex flex-col gap-1"
    >
      <div className="flex items-center gap-1">
        {[0, 1, 2, 3].map((index) => {
          const filled = index < filledCount;
          const segStyle: CSSProperties = {
            ...SEGMENT_BASE,
            background: filled && tone ? tone.filled : TRACK_BG,
          };
          return <span key={index} data-filled={filled || undefined} style={segStyle} />;
        })}
      </div>
      {ready && tone ? (
        <span
          className="text-xs text-secondary"
          data-testid="password-strength-label"
          style={{ color: tone.filled }}
        >
          {t(`signUp.fields.password.strength.${tone.labelKey}`)}
        </span>
      ) : null}
    </div>
  );
}
